import { Component, Input } from '@angular/core';

/**
 * Renders a loyalty-points value followed by the LOMY coin icon — the single
 * source of truth for how points are shown across the console.
 *
 * Rules (see project memory "loyalty-points-terminology"):
 *  - The coin ICON is the visual currency mark (brand pink, never gold).
 *  - The WORD is never "coin"; when spelled out it is "bod/bodovi/bodova" (hr)
 *    or "point/points" (en) via i18n — this component shows the icon, so callers
 *    that need the icon+value use it and drop the word entirely.
 *  - Decimal separator is "." (values are trimmed to max 2 decimals).
 *
 * Usage:
 *   <app-lomy-points [value]="124"></app-lomy-points>        → 124 ◈
 *   <app-lomy-points [value]="12.4"></app-lomy-points>       → 12.4 ◈
 *   <app-lomy-points [value]="82" prefix="+"></app-lomy-points> → +82 ◈
 *   <app-lomy-points iconOnly></app-lomy-points>             → ◈  (e.g. input suffix)
 */
@Component({
  selector: 'app-lomy-points',
  templateUrl: './lomy-points.component.html',
  styleUrls: ['./lomy-points.component.scss'],
})
export class LomyPointsComponent {
  /** Points amount rendered before the coin icon. Omit for icon-only. */
  @Input() value: number | string | null = null;

  /** Coin icon size in px. */
  @Input() size = 16;

  /** Render only the coin icon (no number) — e.g. as an input suffix. */
  @Input() iconOnly = false;

  /** Optional leading string glued to the value, e.g. "+" for awarded points. */
  @Input() prefix = '';

  /**
   * When true the coin icon uses the current text color instead of brand pink —
   * for places where the coin should blend with muted/secondary text (e.g. a
   * balance shown beneath a name) rather than stand out.
   */
  @Input() inheritColor = false;

  /**
   * Formatted value: "." decimal separator, trailing zeros trimmed (max 2
   * decimals), and thousands grouped with a space so large totals (e.g. 42 180)
   * stay readable without clashing with the "." decimal.
   */
  get display(): string {
    if (this.value === null || this.value === undefined || this.value === '') {
      return '';
    }
    const n =
      typeof this.value === 'number'
        ? this.value
        : Number(String(this.value).replace(',', '.'));
    if (!isFinite(n)) return String(this.value);

    const rounded = Math.round(n * 100) / 100;
    const [intPart, decPart] = Math.abs(rounded).toString().split('.');
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const sign = rounded < 0 ? '-' : '';
    return this.prefix + sign + grouped + (decPart ? '.' + decPart : '');
  }
}
